import { useCallback, useMemo } from 'react';

import { Chip, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { CellProps, Column } from 'react-table';
import { useQueryParams } from 'use-query-params';

import { fetchVerboseCandidates } from 'api';
import { LinkButton } from 'components';
import { Table } from 'components/Table';
import { colors } from 'config';
import {
  convertToDateShort,
  flagTypes,
  flagTypeToLabel,
  getPageCount,
  sivStatuses,
} from 'helpers';
import {
  DelimitedNumericArrayParam,
  DateParam,
  StringParam,
  NumberParam,
  BooleanParam,
  createEnumArrayParam,
  createEnumParam,
} from 'helpers/useQueryParams';
import { useUrlPagination, useControlledPagination } from 'hooks';
import {
  ContractOwner,
  FlagType,
  ReviewedFullCandidateProps,
  ReviewStatus,
  SivStatus,
} from 'types';

import {
  CellWrapper,
  DashboardWrapper,
  NullValueMarker,
} from '../styledComponents';
import { AuditorCandidateTableSidebar } from './AuditorCandidateTableSidebar';

const filterConfig = {
  include_siv_ineligible: BooleanParam,
  organization_id: DelimitedNumericArrayParam,
  tranche_id: DelimitedNumericArrayParam,
  birthdate_min: DateParam,
  birthdate_max: DateParam,
  candidate_id: DelimitedNumericArrayParam,
  last_reviewed_date_min: StringParam,
  last_reviewed_date_max: StringParam,
  fuzzy: BooleanParam,
  review_status_includes: createEnumArrayParam<ReviewStatus>([
    'pending_user_review',
    'review_in_progress',
    'did_not_work_insufficient_info',
    'did_not_work_certain',
    'reviewed_and_verified',
    'unable_to_review',
  ]),
  review_status_only: createEnumParam<ReviewStatus>([
    'pending_user_review',
    'review_in_progress',
    'did_not_work_insufficient_info',
    'did_not_work_certain',
    'reviewed_and_verified',
    'unable_to_review',
  ]),
  contract_owner_includes: createEnumArrayParam<ContractOwner>([
    'dod_contract',
    'not_dod_contract',
    'insufficient_information',
  ]),
  contract_owner_only: createEnumParam<ContractOwner>([
    'dod_contract',
    'not_dod_contract',
    'insufficient_information',
  ]),
  flag_types_include: createEnumArrayParam<FlagType>(flagTypes),
  flag_types_exclude: createEnumArrayParam<FlagType>(flagTypes),
  siv_status_id: createEnumArrayParam<SivStatus>(sivStatuses),
  validated_work_days_min: NumberParam,
  validated_work_days_max: NumberParam,
  has_derogs: BooleanParam,
  has_for_cause: BooleanParam,
  firstname: StringParam,
  lastname: StringParam,
  birthdate: StringParam,
  tazkera: StringParam,
  case_number: StringParam,
  passport_number: StringParam,
  supervisor_given_name: StringParam,
  supervisor_surname: StringParam,
  supervisor_email: StringParam,
  employer_email: StringParam,
  employer_occupation: StringParam,
};

export const AuditorCandidateTable = () => {
  const { controlledRows, controlledPageIndex } = useUrlPagination();
  const [filters, setFilters] = useQueryParams(filterConfig, {
    updateType: 'replaceIn',
  });
  const location = useLocation();
  const urlFrom = useMemo(
    () => location.pathname + location.search,
    [location.pathname, location.search],
  );
  const config = useMemo(() => {
    const { case_number, firstname, lastname, passport_number, ...rest } =
      filters;
    const firstnameTrimmed = firstname?.trim();
    const lastnameTrimmed = lastname?.trim();
    const fuzzy =
      filters.firstname ||
      filters.lastname ||
      filters.case_number ||
      filters.passport_number
        ? true
        : undefined;
    return {
      params: {
        page_number: controlledPageIndex,
        limit: controlledRows,
        firstname: firstnameTrimmed,
        lastname: lastnameTrimmed,
        case_number: case_number,
        passport_number: passport_number,
        ...rest,
        fuzzy,
      },
    };
  }, [controlledPageIndex, controlledRows, filters]);
  const { dataArray, dataLoaded, count } = useControlledPagination(
    fetchVerboseCandidates,
    config,
  );
  const { updateControlledPageIndex } = useUrlPagination();

  const globalFiltersApplied = useMemo(
    () => Object.entries(filters).some(([, value]) => value !== undefined),
    [filters],
  );

  // update and return to first page when filter is applied
  const wrapSetCandidateFilter = useCallback(
    (arg: Parameters<typeof setFilters>[0]) => {
      setFilters(arg);
      updateControlledPageIndex(1);
    },
    [setFilters, updateControlledPageIndex],
  );

  const columns = useMemo(
    (): Array<Column<ReviewedFullCandidateProps>> => [
      {
        id: 'inspect',
        Header: () => {
          return (
            <CellWrapper aria-hidden>
              <p aria-label='Inspect candidate' />
            </CellWrapper>
          );
        },
        width: 100,
        Cell: (row: any) => {
          return (
            <CellWrapper className='inspect'>
              <LinkButton
                aria-label={
                  'Inspect ' +
                  row.row.original.firstname +
                  row.row.original.lastname
                }
                variant='text'
                sx={{ width: '70%' }}
                to={{
                  pathname: `reviewed_candidates/${row.row.original.id}`,
                  state: { from: urlFrom },
                }}
              >
                INSPECT
              </LinkButton>
            </CellWrapper>
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'firstname',
        Header: 'Given Name',
        accessor: 'firstname',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'lastname',
        Header: 'Family Name',
        accessor: 'lastname',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'siv_status',
        Header: 'SIV Status',
        accessor: 'siv_status',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'birthdate',
        Header: 'DOB',
        accessor: (row) => convertToDateShort(row.birthdate),
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'siv_id',
        Header: 'NVCSIV ID',
        accessor: 'case_number',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'passport_number',
        Header: 'Passport',
        accessor: 'passport_number',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'tazkera',
        Header: 'Tazkera',
        accessor: 'tazkera',
        Cell: (row: CellProps<ReviewedFullCandidateProps>) => {
          const { tazkera } = row.row.original;
          return tazkera && tazkera !== 'null' ? tazkera : <NullValueMarker />;
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'employer_group',
        Header: 'Employer Name',
        accessor: 'organizations',
        Cell: (row) => {
          const { organizations } = row.row.original;
          if (organizations.length === 0) {
            return (
              <CellWrapper>
                <NullValueMarker />
              </CellWrapper>
            );
          }
          return (
            <Typography variant='body1'>
              {organizations.map((o) => o.name).join(' // ')}
            </Typography>
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'derog',
        Header: 'Derog',
        Cell: (row: any) => {
          const { validated_work_claims } = row.row
            .original as ReviewedFullCandidateProps;
          const hasDerog = validated_work_claims.some(
            (claim) => claim.has_derog,
          );
          return hasDerog ? 'Derog' : <NullValueMarker />;
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'flags',
        Header: 'Flags',
        accessor: 'flags',
        Cell: (row) => {
          const { flags } = row.row.original;

          return flags.length === 0 ? (
            <NullValueMarker />
          ) : (
            flags.map(({ type }, index) => (
              <Chip
                key={type || `unknown_type_${index}`}
                label={flagTypeToLabel(type)}
              />
            ))
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
    ],
    [urlFrom],
  );

  return (
    <DashboardWrapper>
      <>
        <AuditorCandidateTableSidebar
          setFilters={wrapSetCandidateFilter}
          filters={filters}
        />

        <Table<ReviewedFullCandidateProps>
          className='table'
          loading={!dataLoaded}
          count={count}
          rowProps={(row) => {
            return !row.is_reviewable
              ? {
                  style: {
                    backgroundColor: colors.uswds.orange10,
                    borderBottom: '1px solid white',
                  },
                }
              : {};
          }}
          globalFiltersApplied={globalFiltersApplied}
          handleGlobalFilterClear={() => {
            // Reset filters in history state
            wrapSetCandidateFilter({
              include_siv_ineligible: undefined,
              organization_id: undefined,
              tranche_id: undefined,
              birthdate_min: undefined,
              birthdate_max: undefined,
              candidate_id: undefined,
              last_reviewed_date_min: undefined,
              last_reviewed_date_max: undefined,
              fuzzy: undefined,
              review_status_includes: undefined,
              review_status_only: undefined,
              contract_owner_includes: undefined,
              contract_owner_only: undefined,
              flag_types_include: undefined,
              flag_types_exclude: undefined,
              siv_status_id: undefined,
              validated_work_days_min: undefined,
              validated_work_days_max: undefined,
              has_derogs: undefined,
              has_for_cause: undefined,
              firstname: undefined,
              lastname: undefined,
              birthdate: undefined,
              tazkera: undefined,
              case_number: undefined,
              supervisor_given_name: undefined,
              supervisor_surname: undefined,
              supervisor_email: undefined,
              employer_email: undefined,
              employer_occupation: undefined,
              passport_number: undefined,
            });
          }}
          useTableOptions={{
            data: dataArray,
            columns: columns,
            manualPagination: true,
            pageCount: getPageCount(count, controlledRows),
            autoResetPage: false,
          }}
        />
      </>
    </DashboardWrapper>
  );
};
